import { useAppDispatch, useAppSelector } from '../store/hooks';

import { useShortcut } from './useShortcut';
import {
  convertUrlToEnv,
  getQueryParamValue,
  handleClipboardCopy,
  triggerGtmEvent,
} from '../misc/functions';
import { gtmEvents } from '../misc/gtm.constants';
import { showToast } from '../features/toast/toast.slice';
import { resetFilters } from '../features/table/table.actions';
import { openDrawer } from '../features/drawer/drawer.slice';
import { COLUMNS } from '../misc/columns.constants';
import { toggleInfoModalOpened } from '../features/app/app.slice';
import { FILTERS, SIDEBAR } from '../features/drawer/drawer.constants';
import { selectPreparedData } from '../features/table/table.selectors';

export function useShortcuts() {
  const dispatch = useAppDispatch();
  const websitesData = useAppSelector((state) => state.table.websitesData);
  const preparedData = useAppSelector(selectPreparedData);
  const infoModalOpened = useAppSelector((state) => state.app.infoModalOpened);

  const env = websitesData?.['env'];
  const convertLinksTo =
    getQueryParamValue('convertLinksTo') || getQueryParamValue('clt');
  const convertLinks = convertLinksTo && convertLinksTo !== env;

  // copy websites as a comma-separated list
  useShortcut(['CommandOrControl', 'Shift', 'C'], (event) => {
    event.preventDefault();

    const formattedWebsitesList = preparedData
      .map((website) => website[COLUMNS.website])
      .join(',');

    void handleClipboardCopy(formattedWebsitesList);

    triggerGtmEvent(gtmEvents.shortcut, {
      method: 'copy-websites',
    });

    dispatch(showToast('websites list copied', 'success'));
  });

  // copy websites urls
  useShortcut(['Shift', 'Alt', 'C'], (event) => {
    event.preventDefault();

    const websitesUrls = preparedData
      .map((website) => {
        const host =
          (convertLinks &&
            convertUrlToEnv(
              website[COLUMNS.website] as string,
              convertLinksTo,
            )) ||
          (website[COLUMNS.host] as string);

        return `https://${host}/`;
      })
      .join('\n');

    void handleClipboardCopy(websitesUrls);

    triggerGtmEvent(gtmEvents.shortcut, {
      method: 'copy-websites-urls',
    });

    dispatch(showToast('websites urls copied', 'success'));
  });

  // clear filters
  useShortcut(['CommandOrControl', 'Shift', 'E'], (event) => {
    event.preventDefault();

    dispatch(resetFilters());

    triggerGtmEvent(gtmEvents.shortcut, {
      method: 'clear-filters',
      label: event.ctrlKey ? 'windows' : 'macos',
    });

    dispatch(showToast('filters reset', 'success'));
  });

  // toggle header's drawer
  useShortcut(['CommandOrControl', '/'], (event) => {
    dispatch(openDrawer(SIDEBAR));
    triggerGtmEvent(gtmEvents.shortcut, {
      method: 'sidebar-open',
      label: event.ctrlKey ? 'windows' : 'macos',
    });
  });

  // open info modal
  useShortcut(['Shift', '/'], (event) => {
    event.preventDefault();

    dispatch(toggleInfoModalOpened(!infoModalOpened));

    triggerGtmEvent(gtmEvents.openInfoModal);

    triggerGtmEvent(gtmEvents.shortcut, {
      method: 'open-info-modal',
    });
  });

  // search
  useShortcut(['CommandOrControl', 'Shift', 'F'], (event) => {
    event.preventDefault();

    dispatch(openDrawer(FILTERS));
    setTimeout(() => {
      document.querySelector('input')?.select();
    }, 300);

    triggerGtmEvent(gtmEvents.shortcut, {
      method: 'search',
      label: event.ctrlKey ? 'windows' : 'macos',
    });
  });
}
